import { FaceCullingMode, RenderLayer, } from '@skand/three-js-renderer';
import { Color, Quaternion, SphereGeometry, Vector3 } from 'three';
// Frustum frame
const FRUSTUM_VERTICES = [
    new Vector3(-1, -0.75, -1),
    new Vector3(1, -0.75, -1),
    new Vector3(1, 0.75, -1),
    new Vector3(-1, 0.75, -1),
    new Vector3(0, 0, 1),
];
const FRUSTUM_INDICES = [0, 4, 3, 4, 2, 3, 1, 2, 4, 1, 4, 0];
// Ball handle
const BALL_VERTICES = [];
const BALL_INDICES = [];
const sphere = new SphereGeometry(0.25);
const positions = sphere.getAttribute('position');
for (let i = 0; i < positions.count; i++) {
    const x = positions.getX(i);
    const y = positions.getY(i);
    const z = positions.getZ(i);
    BALL_VERTICES.push(new Vector3(x, y, z));
}
if (sphere.index) {
    for (let i = 0; i < sphere.index.array.length; i++) {
        BALL_INDICES.push(sphere.index.array[i]);
    }
}
for (const vertex of BALL_VERTICES) {
    vertex.z++;
}
const n = BALL_VERTICES.length;
BALL_VERTICES.push(new Vector3(0.75, 0.75, -1), new Vector3(-0.75, 0.75, -1), new Vector3(0, 1.5, -1));
BALL_INDICES.push(...[0, 1, 2].map(i => i + n));
/**
 * Create the frustum wire mesh batchable.
 *
 * @param color
 * @param pixelSize
 * @returns
 */
function createFrustumBatchable(color, pixelSize) {
    return {
        type: 'polygon',
        vertices: FRUSTUM_VERTICES,
        indices: FRUSTUM_INDICES,
        position: new Vector3(),
        rotation: new Quaternion(),
        scale: new Vector3(1, 1, 1),
        colors: [color],
        material: {
            type: 'basicOpaque',
            color: new Color(1, 1, 1),
            wireframe: true,
        },
        renderLayer: RenderLayer.AfterPanorama,
        pixelSize,
    };
}
/**
 * Create the solid sphere and up arrow batchable.
 *
 * @param color
 * @param pixelSize
 * @returns
 */
function createSolidBatchable(color, pixelSize) {
    return {
        type: 'polygon',
        vertices: BALL_VERTICES,
        indices: BALL_INDICES,
        position: new Vector3(),
        rotation: new Quaternion(),
        scale: new Vector3(1, 1, 1),
        colors: [color],
        material: {
            type: 'basicOpaque',
            color: new Color(1, 1, 1),
            wireframe: false,
            faceCulling: FaceCullingMode.None,
        },
        renderLayer: RenderLayer.AfterPanorama,
        pixelSize,
    };
}
/**
 * Photo camera model.
 */
export class PhotoCamera {
    constructor(color, pixelSize, meshInstance) {
        Object.defineProperty(this, "batchables", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "meshInstance", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "visible", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.batchables = [
            createFrustumBatchable(color, pixelSize),
            createSolidBatchable(color, pixelSize),
        ];
        this.meshInstance = meshInstance;
        this.visible = false;
    }
    async show() {
        this.visible = true;
        for (const batchable of this.batchables) {
            this.meshInstance.addMesh(batchable);
        }
        return this;
    }
    async hide() {
        this.visible = false;
        for (const batchable of this.batchables) {
            this.meshInstance.removeMesh(batchable);
        }
        return this;
    }
    async destroy() {
        await this.hide();
    }
    isVisible() {
        return this.visible;
    }
    isPicked(pickResult) {
        for (const batchable of this.batchables) {
            const pickingId = this.meshInstance.getPickingId(batchable);
            const instance = this.meshInstance.getInstanceObject(batchable);
            if (pickResult.object === instance && pickResult.userData === pickingId) {
                return true;
            }
        }
        return false;
    }
    getBoundingRadius() {
        return 1;
    }
    getPosition(position) {
        if (!position)
            position = new Vector3();
        position.copy(this.batchables[0].position);
        return position;
    }
    getRotation(rotation) {
        if (!rotation)
            rotation = new Quaternion();
        rotation.copy(this.batchables[0].rotation);
        return rotation;
    }
    getScale(scale) {
        if (!scale)
            scale = new Vector3(1, 1, 1);
        scale.copy(this.batchables[0].scale);
        return scale;
    }
    setPosition(position) {
        for (const batchable of this.batchables) {
            batchable.position.copy(position);
            this.meshInstance.refresh(batchable);
        }
    }
    setRotation(rotation) {
        for (const batchable of this.batchables) {
            batchable.rotation.copy(rotation);
            this.meshInstance.refresh(batchable);
        }
    }
    setScale(scale) {
        for (const batchable of this.batchables) {
            batchable.scale.copy(scale);
            this.meshInstance.refresh(batchable);
        }
    }
    /**
     * Set the color of the camera.
     *
     * @param color
     */
    setColor(color) {
        for (const batchable of this.batchables) {
            batchable.colors[0].copy(color);
            this.meshInstance.refresh(batchable);
        }
    }
}
